import { Button, Chip, EmptyState, ErrorBox, PageSection, Spinner } from "@client/components/ui";
import { ChainTable } from "@client/features/sektory/ChainTable";
import { EmergingList } from "@client/features/sektory/EmergingList";
import { isSectorSortKey, type SectorSortKey } from "@client/features/sektory/lib";
import { SectorDetailPanel } from "@client/features/sektory/SectorDetailPanel";
import { SectorTable } from "@client/features/sektory/SectorTable";
import { api } from "@client/lib/api";
import { sectorsQuery } from "@client/lib/queries";
import { datePl, pct } from "@shared/format";
import { isSectorLabel, type SectorLabel } from "@shared/sectors";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";

const SektorySearchSchema = z.object({
  sektor: z.string().optional(),
  sort: z.string().optional(),
  dir: z.enum(["asc", "desc"]).optional(),
});
export type SektorySearch = z.infer<typeof SektorySearchSchema>;

export const Route = createFileRoute("/sektory")({
  validateSearch: (search): SektorySearch => SektorySearchSchema.parse(search),
  component: SektoryPage,
});

function SektoryPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const qc = useQueryClient();
  // manual "Synchronizuj teraz" fires the sector sync in the background (202, no wait); poll
  // until the radar actually has data, then stop
  const [polling, setPolling] = useState(false);

  const radarQ = useQuery({ ...sectorsQuery(), refetchInterval: polling ? 10_000 : false });

  useEffect(() => {
    if (polling && radarQ.data?.asOf != null) setPolling(false);
  }, [polling, radarQ.data?.asOf]);

  const syncMutation = useMutation({
    mutationFn: () => api.sync.now({ what: "sectors" }),
    onSuccess: () => {
      setPolling(true);
      qc.invalidateQueries({ queryKey: ["sectors"] });
    },
  });

  const selectedLabel: SectorLabel | null =
    search.sektor && isSectorLabel(search.sektor) ? search.sektor : null;
  const sortKey: SectorSortKey = search.sort && isSectorSortKey(search.sort) ? search.sort : "rs7d";
  const dir = search.dir ?? "desc";

  useEffect(() => {
    if (!selectedLabel) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") navigate({ search: (prev) => ({ ...prev, sektor: undefined }) });
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selectedLabel, navigate]);

  if (radarQ.error) {
    return (
      <PageSection>
        <ErrorBox error={radarQ.error} />
      </PageSection>
    );
  }

  const radar = radarQ.data;
  if (!radar) {
    return (
      <PageSection>
        <div className="flex justify-center py-20">
          <Spinner size={28} />
        </div>
      </PageSection>
    );
  }

  return (
    <section className="flex flex-col items-stretch gap-4 p-3 md:flex-row md:items-start md:p-6">
      <div className="flex min-w-0 flex-1 flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h1 className="text-lg font-semibold text-fg-1">Sektory</h1>
          {radar.asOf != null && (
            <div className="flex flex-wrap items-center gap-2 text-xs text-fg-3">
              <span>Stan na {datePl(radar.asOf)}</span>
              <Chip tone="gray">BTC 7d {pct(radar.reference.btc7d)}</Chip>
              <Chip tone="gray">TOTAL3 7d {pct(radar.reference.total3_7d)}</Chip>
            </div>
          )}
        </div>

        {radar.asOf == null ? (
          <EmptyState
            title="Dane pojawią się po pierwszej synchronizacji sektorów."
            action={
              <div className="flex flex-col items-center gap-2">
                <Button variant="alt" loading={syncMutation.isPending} onClick={() => syncMutation.mutate()}>
                  Synchronizuj teraz
                </Button>
                {(syncMutation.isPending || polling) && <Spinner size={20} />}
                {polling && <span className="text-xs text-fg-3">Synchronizacja w toku…</span>}
                {syncMutation.isError && <ErrorBox error={syncMutation.error} />}
              </div>
            }
          />
        ) : (
          <>
            <SectorTable
              rows={radar.sectors}
              sortKey={sortKey}
              dir={dir}
              selectedLabel={selectedLabel}
              onSortChange={(key) =>
                navigate({
                  search: (prev) => ({
                    ...prev,
                    sort: key,
                    dir: sortKey === key ? (dir === "desc" ? "asc" : "desc") : "desc",
                  }),
                })
              }
              onSelect={(label) => navigate({ search: (prev) => ({ ...prev, sektor: label }) })}
            />
            <ChainTable rows={radar.chains} />
            <EmergingList rows={radar.emerging} />
          </>
        )}
      </div>
      {selectedLabel && (
        <SectorDetailPanel
          key={selectedLabel}
          label={selectedLabel}
          onClose={() => navigate({ search: (prev) => ({ ...prev, sektor: undefined }) })}
        />
      )}
    </section>
  );
}
